<script setup lang="ts">
/**
 * Progress of a batch of uploads: a head with „3 von 5 hochgeladen“ (or the
 * file in flight, or the failures) and an overall bar, and under it one
 * attachment chip per file with its own state. Queued and running uploads can
 * be cancelled (`cancel`), failed or cancelled ones retried (`retry`); the
 * head clears the finished ones (`clear-finished`).
 *
 * The list collapses (`v-model:collapsed`); `autoCollapse` folds it once
 * nothing is queued or running any more. The summary line is a polite live
 * region, so progress is announced without stealing focus.
 */
import Check from 'lucide-vue-next/dist/esm/icons/check.js'; import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js'; import RotateCw from 'lucide-vue-next/dist/esm/icons/rotate-cw.js'; import X from 'lucide-vue-next/dist/esm/icons/x.js';
import { computed, useId, watch } from 'vue';
import { NxAttachmentChip } from '../Attachment';
import { NxButton } from '../Button';
import { NxIconButton } from '../IconButton';
import { NxProgressBar } from '../ProgressBar';

export type NxUploadStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled';

export interface NxUploadItem {
  id: string | number;
  name: string;
  /** Size in bytes. */
  size?: number;
  /** MIME type, for the file icon. */
  type?: string;
  status: NxUploadStatus;
  /** 0–1 while uploading; without it the item's bar runs indeterminate. */
  progress?: number | null;
  /** Message of a failed upload. */
  error?: string;
}

const props = withDefaults(
  defineProps<{
    items: readonly NxUploadItem[];
    /** Fold the list once nothing is queued or running any more. */
    autoCollapse?: boolean;
    /** `{done}` / `{total}` are replaced. */
    summaryLabel?: string;
    /** `{name}` is replaced. */
    activeLabel?: string;
    /** `{count}` is replaced. */
    failedLabel?: string;
    queuedLabel?: string;
    doneLabel?: string;
    cancelledLabel?: string;
    cancelLabel?: string;
    retryLabel?: string;
    clearLabel?: string;
    toggleLabel?: string;
    /** Accessible name of the region. */
    ariaLabel?: string;
    locale?: string;
  }>(),
  {
    autoCollapse: false,
    summaryLabel: '{done} von {total} hochgeladen',
    activeLabel: 'Wird hochgeladen: {name}',
    failedLabel: '{count} fehlgeschlagen',
    queuedLabel: 'Wartet',
    doneLabel: 'Fertig',
    cancelledLabel: 'Abgebrochen',
    cancelLabel: 'Abbrechen',
    retryLabel: 'Erneut versuchen',
    clearLabel: 'Erledigte entfernen',
    toggleLabel: 'Liste ein- oder ausklappen',
    ariaLabel: 'Uploads',
    locale: undefined,
  }
);

const collapsed = defineModel<boolean>('collapsed', { default: false });

const emit = defineEmits<{
  cancel: [id: NxUploadItem['id']];
  retry: [id: NxUploadItem['id']];
  'clear-finished': [];
}>();

const counted = computed(() => props.items.filter((i) => i.status !== 'cancelled'));
const doneCount = computed(() => counted.value.filter((i) => i.status === 'done').length);
const errorCount = computed(() => props.items.filter((i) => i.status === 'error').length);
const running = computed(() => props.items.find((i) => i.status === 'uploading') ?? null);
const busy = computed(() =>
  props.items.some((i) => i.status === 'uploading' || i.status === 'queued')
);
const finishedCount = computed(
  () => props.items.filter((i) => i.status === 'done' || i.status === 'cancelled').length
);

/** Share of the batch done: finished files count 1, running ones their progress. */
const overall = computed(() => {
  const list = counted.value;
  if (!list.length) return 0;
  const sum = list.reduce((acc, i) => {
    if (i.status === 'done' || i.status === 'error') return acc + 1;
    if (i.status === 'uploading') return acc + clamp(i.progress ?? 0);
    return acc;
  }, 0);
  return sum / list.length;
});

function clamp(v: number): number {
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0;
}

const summary = computed(() => {
  if (running.value) return props.activeLabel.replace('{name}', running.value.name);
  const base = props.summaryLabel
    .replace('{done}', String(doneCount.value))
    .replace('{total}', String(counted.value.length));
  return errorCount.value
    ? `${base} · ${props.failedLabel.replace('{count}', String(errorCount.value))}`
    : base;
});

// While anything still uploads the bar stays neutral; the verdict (a failure,
// or all done) shows once the queue has settled.
const tone = computed(() => {
  if (busy.value) return 'brand' as const;
  if (errorCount.value) return 'danger' as const;
  if (counted.value.length) return 'success' as const;
  return 'brand' as const;
});

watch(busy, (isBusy, wasBusy) => {
  if (props.autoCollapse && wasBusy && !isBusy) collapsed.value = true;
});

function chipState(item: NxUploadItem): 'idle' | 'uploading' | 'error' {
  if (item.status === 'uploading') return 'uploading';
  if (item.status === 'error') return 'error';
  return 'idle';
}

function chipMeta(item: NxUploadItem): string | undefined {
  if (item.status === 'queued') return props.queuedLabel;
  if (item.status === 'cancelled') return props.cancelledLabel;
  if (item.status === 'done' && item.size === undefined) return props.doneLabel;
  return undefined;
}

const listId = useId();
</script>

<template>
  <section
    class="nx-upload-queue"
    :class="{ 'is-collapsed': collapsed }"
    :aria-label="props.ariaLabel"
    data-testid="nx-upload-queue"
  >
    <header class="nx-upload-queue__head">
      <span class="nx-upload-queue__state" :class="`tone-${tone}`" aria-hidden="true">
        <Check v-if="tone === 'success'" class="nxt-icon-sm" />
        <span v-else class="nx-upload-queue__count">{{ doneCount }}/{{ counted.length }}</span>
      </span>
      <div class="nx-upload-queue__summary">
        <strong role="status" aria-live="polite" data-testid="nx-upload-queue-summary">
          {{ summary }}
        </strong>
        <NxProgressBar :value="overall" :tone="tone" size="sm" :label="props.ariaLabel" />
      </div>
      <NxButton
        v-if="finishedCount"
        height="xs"
        variant="ghost"
        data-testid="nx-upload-queue-clear"
        @click="emit('clear-finished')"
      >
        {{ props.clearLabel }}
      </NxButton>
      <NxIconButton
        height="s"
        :title="props.toggleLabel"
        :aria-label="props.toggleLabel"
        :aria-expanded="!collapsed"
        :aria-controls="listId"
        data-testid="nx-upload-queue-toggle"
        @click="collapsed = !collapsed"
      >
        <ChevronDown class="nx-upload-queue__chevron nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
    </header>
    <ul v-show="!collapsed" :id="listId" class="nx-upload-queue__list" role="list">
      <li
        v-for="item in props.items"
        :key="item.id"
        class="nx-upload-queue__item"
        :class="`status-${item.status}`"
        data-testid="nx-upload-queue-item"
      >
        <NxAttachmentChip
          class="nx-upload-queue__chip"
          :name="item.name"
          :size="item.size ?? null"
          :type="item.type"
          :meta="chipMeta(item)"
          :state="chipState(item)"
          :progress="item.progress ?? null"
          :error="item.error"
          :locale="props.locale"
        >
          <template #actions>
            <NxIconButton
              v-if="item.status === 'queued' || item.status === 'uploading'"
              height="xs"
              :title="props.cancelLabel"
              :aria-label="`${props.cancelLabel}: ${item.name}`"
              data-testid="nx-upload-queue-cancel"
              @click="emit('cancel', item.id)"
            >
              <X class="nxt-icon-sm" aria-hidden="true" />
            </NxIconButton>
            <NxIconButton
              v-else-if="item.status === 'error' || item.status === 'cancelled'"
              height="xs"
              :title="props.retryLabel"
              :aria-label="`${props.retryLabel}: ${item.name}`"
              data-testid="nx-upload-queue-retry"
              @click="emit('retry', item.id)"
            >
              <RotateCw class="nxt-icon-sm" aria-hidden="true" />
            </NxIconButton>
            <Check
              v-else-if="item.status === 'done'"
              class="nx-upload-queue__done nxt-icon-sm"
              aria-hidden="true"
            />
          </template>
        </NxAttachmentChip>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.nx-upload-queue {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-upload-queue__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-upload-queue__state {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  min-width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
}
.nx-upload-queue__state.tone-success {
  background: var(--nxt-success-subtle-bg);
  color: var(--nxt-success-subtle-fg);
}
.nx-upload-queue__state.tone-danger {
  background: var(--nxt-danger-subtle-bg);
  color: var(--nxt-danger-subtle-fg);
}
.nx-upload-queue__summary {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-upload-queue__summary strong {
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-upload-queue__chevron {
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-upload-queue.is-collapsed .nx-upload-queue__chevron {
  transform: rotate(-90deg);
}
.nx-upload-queue__list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  max-height: calc(var(--nxt-size-unit) * 18);
  margin: 0;
  padding: 0;
  overflow-y: auto;
  font-size: inherit;
  list-style: none;
  overscroll-behavior: contain;
}
.nx-upload-queue__chip {
  width: 100%;
}
.nx-upload-queue__item.status-cancelled .nx-upload-queue__chip {
  opacity: 0.6;
}
.nx-upload-queue__done {
  margin-inline: var(--nxt-space-xs);
  color: var(--nxt-success-subtle-fg);
}
@media (prefers-reduced-motion: reduce) {
  .nx-upload-queue__chevron {
    transition: none;
  }
}
</style>
